import { useHistory } from 'react-router'
import { CircleAlert, MapPin, Trash2 } from 'lucide-react'
import {
  CartHeader,
  CartItems,
  CartSummary,
  CouponSection,
  FreeShippingProgress,
  PaymentMethods,
  SuggestedProducts
} from './components'

import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import { formatDateBR, getCartSplitDeliveryEstimateTexts } from '@cannect/utils/deliveryEstimate'
import { hasQuotedNationalFreight } from '@cannect/utils/nationalFreight'
import { getPayableAmount } from '@cannect/utils/reshipping'
import { formatToReal } from '@cannect/utils/number'
import useSignedUser from 'hooks/useSignedUser'

import { ProgressiveGroupSection } from 'new-components/ProgressiveGroupCard/ProgressiveGroupSection'
import { ProgressiveSingleQuantityControl } from 'new-components/ProgressiveQuantityDropdown'
import { useCallback, useMemo, useState } from 'react'

interface CartSheetProps {
  isOpen: boolean
  onClose: () => void
}

interface AnonCartItemRowProps {
  item: any
  onRemove: (itemId: number) => void
  onQuantityChange: (itemId: number, quantity: number) => void
  isLoading?: boolean
}

/** Linha simplificada do carrinho anônimo — só o essencial (preço de lista, sem cupom/progressivo/sabores). */
function AnonCartItemRow({ item, onRemove, onQuantityChange, isLoading = false }: AnonCartItemRowProps) {
  const quantity = item?.quantity || 0
  const unitPrice = Number(item?.product?.price) || 0

  return (
    <div
      className={`relative flex items-start gap-3 rounded-lg border border-gray-100 bg-white p-3 transition-opacity sm:p-4 ${isLoading ? 'pointer-events-none opacity-60' : ''}`}>
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60">
          <svg className="h-5 w-5 animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        </div>
      )}
      <img
        src={item?.product?.image}
        alt={item?.product?.name}
        className="h-[80px] w-[80px] flex-shrink-0 rounded-lg object-cover sm:h-[105px] sm:w-[125px]"
      />

      <div className="flex flex-1 flex-col gap-2">
        <div className="flex justify-end">
          <button
            onClick={() => onRemove(item.id)}
            className="flex items-center gap-1 rounded-md border-none bg-transparent p-1 text-[#E87883] outline-none hover:text-[#D35D6A] focus:outline-none focus:ring-2 focus:ring-[#6B7B3C] focus:ring-offset-1">
            <Trash2 className="h-4 w-4" />
            <span className="text-xs font-medium">Remover</span>
          </button>
        </div>

        <p className="text-xs font-medium text-gray-800 sm:text-sm">{item?.product?.name}</p>

        <div className="mt-2 flex flex-row items-center justify-between gap-2">
          <ProgressiveSingleQuantityControl
            productId={item?.product?.id || 0}
            qty={quantity}
            basePrice={unitPrice}
            overlay
            onSelect={(n) => onQuantityChange(item.id, n)}
            onRemove={() => onRemove(item.id)}
          />
          <span className="text-sm font-semibold text-gray-900">{formatToReal(unitPrice)}</span>
        </div>
      </div>
    </div>
  )
}

export default function CartSheet({ isOpen, onClose }: CartSheetProps) {
  const history = useHistory()
  const { signedUser } = useSignedUser()
  const isAuthenticated = !!signedUser?.id
  const {
    lastOrderCreated,
    removeCart,
    clearCart,
    cartLoading,
    incrementOrDecrementCart,
    isUpdate,
    addToCart,
    coupon,
    removeCoupon,
    updateCartCoupon,
    deliveryCep,
    setDeliveryCep,
    validateDeliveryCep,
    validatingDeliveryCep,
    anonOrderData,
    updateAnonItem,
    removeAnonItem,
    anonShippingData,
    validateAnonDeliveryCep
  } = usePrepareCart()

  const anonItems = anonOrderData?.items || []
  const anonSubtotal = useMemo(
    () =>
      anonItems.reduce(
        (sum: number, item: any) => sum + (Number(item?.product?.price) || 0) * (Number(item?.quantity) || 0),
        0
      ),
    [anonItems]
  )

  // Loading por item do carrinho anônimo (chave: id do item, não product_id)
  const [loadingAnonItemIds, setLoadingAnonItemIds] = useState<Set<number>>(new Set())
  const addAnonLoading = useCallback((id: number) => setLoadingAnonItemIds((s) => new Set([...s, id])), [])
  const removeAnonLoading = useCallback(
    (id: number) =>
      setLoadingAnonItemIds((s) => {
        const next = new Set(s)
        next.delete(id)
        return next
      }),
    []
  )

  const handleUpdateAnonQuantity = useCallback(
    async (itemId: number, newQuantity: number) => {
      if (newQuantity < 0 || loadingAnonItemIds.has(itemId)) return
      addAnonLoading(itemId)
      try {
        await updateAnonItem(itemId, newQuantity)
      } finally {
        removeAnonLoading(itemId)
      }
    },
    [loadingAnonItemIds, addAnonLoading, removeAnonLoading, updateAnonItem]
  )

  const handleRemoveAnonItem = useCallback(
    async (itemId: number) => {
      if (loadingAnonItemIds.has(itemId)) return
      addAnonLoading(itemId)
      try {
        await removeAnonItem(itemId)
      } finally {
        removeAnonLoading(itemId)
      }
    },
    [loadingAnonItemIds, addAnonLoading, removeAnonLoading, removeAnonItem]
  )

  const cartItems = isAuthenticated ? lastOrderCreated?.items || [] : anonItems
  const subtotal = lastOrderCreated?.subtotal || 0
  const shipping = lastOrderCreated?.shipping || 0
  const discount = lastOrderCreated?.discount || 0
  const total = getPayableAmount(lastOrderCreated)
  const noImportCost = lastOrderCreated?.noImportCost || false
  const shippingDiscount = lastOrderCreated?.shippingDiscount || 0
  const nationalFreight = lastOrderCreated?.national_freight || 0

  // Valores efetivos de frete para o carrinho anônimo
  const effectiveAnonShipping = anonShippingData?.shipping ?? (anonOrderData?.preliminary_shipping ?? 0)
  const effectiveAnonFreight = anonShippingData?.national_freight ?? 0
  const anonTotal = anonSubtotal + effectiveAnonShipping + effectiveAnonFreight
  const anonNoImportCost = effectiveAnonShipping === 0 && effectiveAnonFreight === 0
  const shippingRestriction = isAuthenticated
    ? (lastOrderCreated?.shipping_restriction ?? lastOrderCreated?.summary?.shipping_restriction ?? null)
    : (anonShippingData?.shipping_restriction ?? null)

  // Itens no formato esperado pelo preview de desconto progressivo
  const previewItems = useMemo(
    () =>
      cartItems.map((item: any) => ({
        product_id: item?.product?.id || item?.product_id || 0,
        quantity: item?.quantity || 0
      })),
    [cartItems]
  )

  // IDs dos produtos em grupos válidos — excluídos da lista de itens únicos
  const [groupedIds, setGroupedIds] = useState<Set<number>>(new Set())
  const handleGroupedIdsChange = useCallback((ids: Set<number>) => {
    setGroupedIds(ids)
  }, [])

  // Filtra itens únicos excluindo os que estão em grupos
  const singleItems = useMemo(
    () => cartItems.filter((item: any) => !groupedIds.has(item?.product?.id)),
    [cartItems, groupedIds]
  )

  // Loading por produto: previne req simultâneas e exibe spinner na linha
  const [loadingProductIds, setLoadingProductIds] = useState<Set<number>>(new Set())
  const addLoading = useCallback((id: number) => setLoadingProductIds((s) => new Set([...s, id])), [])
  const removeLoading = useCallback(
    (id: number) =>
      setLoadingProductIds((s) => {
        const next = new Set(s)
        next.delete(id)
        return next
      }),
    []
  )

  // Sinaliza qual produto teve quantidade alterada (abre oferta cross-sell de item único)
  const [qtyChangedProductId, setQtyChangedProductId] = useState<number | undefined>(undefined)

  const freeShippingThreshold = 2500
  const freeShippingMessage = 'Faltam {amount} para Custo de importação grátis'

  const handleRemoveItem = (productId: number) => {
    removeCart(productId)
  }

  const handleUpdateQuantity = useCallback(
    async (productId: number, newQuantity: number) => {
      if (newQuantity <= 0 || loadingProductIds.has(productId)) return
      addLoading(productId)
      try {
        await incrementOrDecrementCart(productId, newQuantity)
        setQtyChangedProductId(productId)
      } finally {
        removeLoading(productId)
      }
    },
    [loadingProductIds, addLoading, removeLoading, incrementOrDecrementCart]
  )

  const tazBlocked =
    shippingRestriction !== null &&
    (shippingRestriction.covered === false ||
      (shippingRestriction.requires_cep && !deliveryCep))

  const hasUnavailableItems = !!(lastOrderCreated?.summary?.has_unavailable_items ?? lastOrderCreated?.has_unavailable_items)
  const unavailableItems: { product_id: number; name: string }[] =
    lastOrderCreated?.summary?.unavailable_items ?? lastOrderCreated?.unavailable_items ?? []

  const anonCepValid = !isAuthenticated && (deliveryCep || '').replace(/\D/g, '').length === 8

  const handleCheckout = () => {
    if (!isAuthenticated) {
      if (!anonCepValid || tazBlocked) return
      history.push('/login?redirect=/carrinho')
      onClose()
      return
    }

    if (!isUpdate && !tazBlocked && !hasUnavailableItems) {
      history.push('/carrinho')
      onClose()
    }
  }

  const handleAddSuggestedProduct = (product: any) => {
    if (!isUpdate) {
      addToCart(product.id, 1, product.class_id, product.brand_id, product.type)
    }
  }

  const handleCepChange = (value: string) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 8)
    const masked = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits
    setDeliveryCep(masked)
  }

  const formatEstimatedRangeText = (start?: string | null, end?: string | null) => {
    if (!start || !end) return null
    return `A entrega estimada é entre ${formatDateBR(start)} e ${formatDateBR(end)}.`
  }

  // Anônimo e logado compartilham os mesmos campos de estimativa — só a origem do dado muda.
  const deliverySource = isAuthenticated ? lastOrderCreated : { ...(anonShippingData || {}), items: anonItems }

  const estimatedRangeText = formatEstimatedRangeText(
    deliverySource?.estimated_delivery_range_start,
    deliverySource?.estimated_delivery_range_end
  )
  const splitEstimateTexts = getCartSplitDeliveryEstimateTexts(deliverySource)

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-30 backdrop-blur-sm transition-all duration-300"
          onClick={onClose}
          role="button"
          tabIndex={0}
          aria-label="Fechar carrinho"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClose()
            }
          }}
        />
      )}

      <div
        className={`fixed right-0 top-0 z-50 h-full w-full bg-white shadow-xl transition-transform duration-300 ease-in-out sm:w-[400px] lg:w-[500px] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
        <CartHeader onClose={onClose} />

        <hr className="h-[1px] w-full border-0 bg-secondary-200" />

        <div className="h-[calc(100vh-96px)] overflow-y-auto px-4 pb-6 sm:px-8">
          {/* Contador + remover tudo — usa o total real (inclui itens agrupados) */}
          {cartItems.length > 0 && (
            <div className="mb-3 mt-4 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                {cartItems.length} {cartItems.length === 1 ? 'item' : 'itens'}
              </span>
              {isAuthenticated && (
                <button
                  onClick={() => clearCart()}
                  disabled={cartLoading || isUpdate}
                  className="flex cursor-pointer items-center gap-1 rounded-md border-0 bg-transparent px-2 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50">
                  <Trash2 className="h-3.5 w-3.5 text-[#E87883]" />
                  <span className="text-xs font-medium text-[#E87883]">
                    {cartLoading ? 'Atualizando...' : 'Remover tudo'}
                  </span>
                </button>
              )}
            </div>
          )}

          <>
            {/* Grupos de desconto progressivo — apenas autenticados */}
            {cartItems.length > 0 && isAuthenticated && (
              <ProgressiveGroupSection
                cartItems={previewItems}
                onGroupedIdsChange={handleGroupedIdsChange}
                mode="sheet"
                qtyChangedProductId={qtyChangedProductId}
              />
            )}

            {/* Itens do carrinho */}
            {isAuthenticated ? (
              <CartItems
                items={singleItems}
                onRemove={handleRemoveItem}
                onQuantityChange={handleUpdateQuantity}
                totalCartItems={cartItems.length}
                loadingProductIds={loadingProductIds}
              />
            ) : anonItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-4 h-16 w-16 text-gray-300">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01"
                    />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-medium text-gray-900">Seu carrinho está vazio</h3>
                <p className="text-sm text-gray-500">Adicione produtos para começar suas compras</p>
              </div>
            ) : (
              <div className="space-y-4">
                {anonItems.map((item: any) => (
                  <AnonCartItemRow
                    key={item.id}
                    item={item}
                    onRemove={handleRemoveAnonItem}
                    onQuantityChange={handleUpdateAnonQuantity}
                    isLoading={loadingAnonItemIds.has(item.id)}
                  />
                ))}
              </div>
            )}

            {cartItems.length > 0 && isAuthenticated && !noImportCost && (
              <FreeShippingProgress
                subtotal={parseFloat(subtotal.toString())}
                threshold={freeShippingThreshold}
                message={freeShippingMessage}
              />
            )}

            {cartItems.length > 0 && (
              <CartSummary
                subtotal={isAuthenticated ? subtotal : anonSubtotal}
                shipping={isAuthenticated ? shipping : effectiveAnonShipping}
                discount={discount}
                shippingDiscount={shippingDiscount}
                total={isAuthenticated ? total : anonTotal}
                noImportCost={isAuthenticated ? noImportCost : anonNoImportCost}
                nationalFreight={isAuthenticated ? nationalFreight : effectiveAnonFreight}
                nationalFreightQuoted={hasQuotedNationalFreight(lastOrderCreated)}
              />
            )}

            {cartItems.length > 0 && (
              <div className="mb-3 mt-3 space-y-2">
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Digite seu CEP:"
                    className="w-[calc(100%-90px)] rounded-md border-0 bg-[#EDECE7] py-2 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6B7B3C] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={deliveryCep || ''}
                    onChange={(e) => handleCepChange(e.target.value)}
                    disabled={isUpdate || validatingDeliveryCep}
                  />
                  <div className="absolute right-0 top-1/2 -translate-y-1/2">
                    <button
                      className="rounded-md border-none bg-primary-700 px-3 py-2 text-sm text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:bg-gray-400"
                      onClick={isAuthenticated ? validateDeliveryCep : validateAnonDeliveryCep}
                      disabled={
                        isUpdate || validatingDeliveryCep || (deliveryCep || '').replace(/\D/g, '').length !== 8
                      }>
                      {validatingDeliveryCep ? 'Validando...' : 'Validar'}
                    </button>
                  </div>
                </div>

                {(splitEstimateTexts || estimatedRangeText) && shippingRestriction?.covered !== false && (
                  <div className="flex h-auto w-full items-center gap-4 rounded border border-yellow-200 bg-[#FFEEC299] px-4 py-2.5">
                    <div className="flex h-4 w-4 items-center justify-center rounded-full">
                      <CircleAlert className="h-4 w-4 text-neutral-500" />
                    </div>
                    {splitEstimateTexts ? (
                      <div className="flex flex-col gap-1">
                        {splitEstimateTexts.map((text, index) => (
                          <p key={text} className="text-xs font-normal text-[#898483]">
                            {index === 0 ? (
                              <>
                                <span className="font-bold">IMPORTANTE:</span> {text}
                              </>
                            ) : (
                              text
                            )}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs font-normal text-[#898483]">
                        <span className="font-bold">IMPORTANTE:</span> {estimatedRangeText}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {cartItems.length > 0 && hasUnavailableItems && (
              <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                <p className="mb-1 font-semibold">Carrinho bloqueado — itens indisponíveis</p>
                <p className="mb-1">Remova os itens abaixo para continuar:</p>
                {unavailableItems.length > 0 && (
                  <ul className="ml-3 list-disc">
                    {unavailableItems.map((p) => (
                      <li key={p.product_id}>{p.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {cartItems.length > 0 && shippingRestriction?.covered === false && (
              <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                <p className="mb-1 font-semibold">
                  Infelizmente não conseguimos entregar este produto na sua região.
                </p>
                <p className="mb-1">Os itens abaixo exigem transporte especial que não atende ao CEP informado.</p>
                {shippingRestriction.blocked_products?.length > 0 && (
                  <ul className="ml-3 list-disc">
                    {shippingRestriction.blocked_products.map((p: { product_id: number; name: string }) => (
                      <li key={p.product_id}>{p.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {cartItems.length > 0 && shippingRestriction?.requires_cep && !deliveryCep && (
              <p className="mb-2 text-xs text-amber-700">
                Informe o CEP para verificar a disponibilidade de transporte antes de avançar.
              </p>
            )}

            {cartItems.length > 0 && isAuthenticated && (
              <CouponSection
                onCheckout={handleCheckout}
                onContinueShopping={onClose}
                onApplyCoupon={updateCartCoupon}
                onRemoveCoupon={removeCoupon}
                coupon={coupon}
                itens={cartItems}
                isUpdating={isUpdate}
                blocked={tazBlocked || hasUnavailableItems}
              />
            )}

            {cartItems.length > 0 && !isAuthenticated && !anonCepValid && (
              <p className="mt-3 text-center text-xs text-amber-700">
                Informe e valide o CEP para continuar para o pagamento.
              </p>
            )}

            {cartItems.length > 0 && !isAuthenticated && (
              <button
                onClick={handleCheckout}
                disabled={!anonCepValid || tazBlocked}
                style={{ outline: 'none' }}
                className="mt-2 h-[44px] w-full rounded border-0 bg-[#6B7B3C] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5A6A2B] focus:outline-none focus:ring-2 focus:ring-[#6B7B3C] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:h-[40px] sm:px-16 sm:text-base">
                Ir para pagamento
              </button>
            )}

            {cartItems.length > 0 && <PaymentMethods />}

            {cartItems.length > 0 && <SuggestedProducts onAddToCart={handleAddSuggestedProduct} />}
          </>
        </div>
      </div>
    </>
  )
}
